import React, { useState, useEffect } from 'react';
import Popup from '../Popup';
import ImageSelector from '../ImageSelector';
import ImageCropper from '../ImageCropper';
import useApi from '../../hooks/useApi';
import useUploader from '../../hooks/useUploader';
import ArrowIcon from '../../../public/assets/images/icons/arrow-up.svg';
import SpinningIcon from '../../../public/assets/images/theme-2/loops-icon-white.svg';
import {
  ColumnWrapper,
  Wrapper,
  GeneralHubImageWrapper,
  HubImageWrapper,
  HubImage,
  SectionHeader,
  Categories,
  Category,
  UploadButton,
  Preview,
  ScrollLeft,
  ScrollRight,
  BottomWrapper,
  ScrollWrapper,
  OuterWrapper,
  Placeholder,
  PreviewCrop,
} from './styles';

const HubImageManager = ({ loopId, currentImage, userToken, fileService, popupIsOpen, onSave, onClose, onRender }) => {
  const categories = ['patterns', 'landscapes', 'people', 'activities', 'animals', 'nature', 'illustrations', 'mixed'];
  const { getData: getGeneralData, loading: loadingGeneralData } = useApi(userToken);
  const { getData: getUserData, loading: loadingUserData } = useApi(userToken);
  const { uploadFile, response, loading: uploadingBlob } = useUploader(fileService);
  const [uploading, setUploading] = useState(false);
  const [selectedHubImage, setSelectedHubImage] = useState();
  const [newImage, setNewImage] = useState();
  const [lastImage, setLastImage] = useState();
  const [preview, setPreview] = useState();
  const [scroll, setScroll] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('patterns');
  const [generalImages, setGeneralImages] = useState([]);
  const [userImages, setUserImages] = useState([]);
  const [blob, setBlob] = useState();
  const scrollSize = 927;
  const snapWidth = 1024;

  useEffect(() => {
    onRender();
  });

  useEffect(() => {
    const getGeneralImages = async () => {
      if (generalImages.length === 0) {
        const response = await getGeneralData(`/api/v3/library/images`);
        if (response.success) {
          setGeneralImages(response.body);
        }
      }
    };
    if (popupIsOpen) {
      getGeneralImages();
    } else {
      setTimeout(() => clearState(), 500);
    }
  }, [popupIsOpen]);

  useEffect(() => {
    const getUserImages = async () => {
      if (userImages.length === 0 || lastImage) {
        const lastImageQuery = lastImage ? `/${lastImage}` : '';
        const response = await getUserData(`/api/v3/circles/${loopId}/images${lastImageQuery}`);
        if (response.success) {
          const images = response.body;
          setUserImages(userImages.concat(images));
          if (images.length >= 10) {
            setLastImage(images[images.length - 1].replace('220x220/', ''));
          }
        }
      }
    };
    if (popupIsOpen) {
      getUserImages();
    } else {
      setTimeout(() => clearState(), 500);
    }
  }, [popupIsOpen, lastImage]);

  useEffect(() => {
    if (currentImage) {
      setSelectedHubImage(currentImage);
    }
  }, [popupIsOpen]);

  useEffect(() => {
    if (response.ok && !uploadingBlob) {
      onSave(response.data.filename);
    }
  }, [response.ok, uploadingBlob]);

  const clearState = () => {
    const existsInUserImages =
      !selectedHubImage || (selectedHubImage && userImages.find((image) => image.includes(selectedHubImage)));
    if (!existsInUserImages) {
      setUserImages([]);
    }
    setSelectedHubImage();
    setNewImage();
    setScroll(0);
    setBlob();
    setLastImage();
  };

  const getImagePath = (imageUrl) => (imageUrl ? `${fileService.url}${imageUrl}` : null);

  const selectCategory = (category) => {
    setSelectedCategory(category);
  };

  const canScrollLeft = () => {
    return scroll < 0;
  };

  const canScrollRight = () => {
    if (!userImages) {
      return false;
    }
    const maxScroll = -Math.floor(userImages.length / 9) * scrollSize;
    return scroll > maxScroll;
  };

  const scrollLeft = () => {
    if (canScrollLeft()) {
      setScroll(scroll + scrollSize);
    }
  };

  const scrollRight = () => {
    if (canScrollRight()) {
      setScroll(scroll - scrollSize);
    }
  };

  const selectImage = (image) => {
    setSelectedHubImage(image);
  };

  const resizeImage = (image) => `220x220/${image.replace('220x220/', '')}`;

  const onUpload = (imageFile) => {
    setNewImage(resizeImage(imageFile.filename));
    setSelectedHubImage(imageFile.filename);
  };

  const getOriginalFilePath = (image) => {
    return image ? fileService.url + getOriginalFileName(image) : null;
  };

  const getOriginalFileName = (image) => {
    return image ? image.split('/').pop() : null;
  };

  const uploadAndSave = async () => {
    if (blob) {
      uploadFile(blob);
    } else {
      onSave(getOriginalFileName(selectedHubImage));
    }
  };

  const onLoading = (loading) => {
    setUploading(loading);
  };

  const generalSection = (
    <ColumnWrapper>
      <SectionHeader>{i18n.__('hub_image_general_section_header')}</SectionHeader>
      <Categories>
        {categories.map((category) => {
          const select = () => selectCategory(category);
          return (
            <Category
              tabIndex={0}
              key={category}
              onClick={select}
              onKeyPress={select}
              selected={category === selectedCategory}
            >
              {i18n.__(`hub_image_category.${category}`)}
            </Category>
          );
        })}
      </Categories>
      <GeneralHubImageWrapper snapWidth={snapWidth}>
        {loadingGeneralData ? (
          <Placeholder icon={SpinningIcon} />
        ) : (
          generalImages
            .filter((image) => image.indexOf(selectedCategory) > 0)
            .map((image, index) => {
              const select = () => selectImage(image);
              return (
                <HubImage
                  tabIndex={0}
                  key={index}
                  title={i18n.__('hub_image_select_image')}
                  src={getImagePath(image)}
                  onClick={select}
                  onKeyPress={select}
                />
              );
            })
        )}
      </GeneralHubImageWrapper>
    </ColumnWrapper>
  );

  const userSection = (
    <ColumnWrapper>
      <SectionHeader>
        {i18n.__('hub_image_user_section_header')}
        {userImages && ` (${userImages.length} ${i18n.__('hub_image_user_section_header_images')})`}
      </SectionHeader>
      <OuterWrapper>
        <ScrollLeft
          tabIndex={0}
          title={i18n.__('hub_image_scroll_left')}
          src={ArrowIcon}
          onClick={scrollLeft}
          onKeyPress={scrollLeft}
          canScroll={canScrollLeft()}
        />
        <HubImageWrapper>
          <ScrollWrapper scroll={scroll}>
            {loadingUserData && userImages.length === 0 ? (
              <Placeholder icon={SpinningIcon} />
            ) : (
              userImages.map((image, index) => {
                const select = () => selectImage(image);
                return (
                  <HubImage
                    tabIndex={0}
                    key={index}
                    title={i18n.__('hub_image_select_image')}
                    src={getImagePath(image)}
                    onClick={select}
                    onKeyPress={select}
                  />
                );
              })
            )}
          </ScrollWrapper>
        </HubImageWrapper>
        <ScrollRight
          tabIndex={0}
          title={i18n.__('hub_image_scroll_right')}
          src={ArrowIcon}
          onClick={scrollRight}
          onKeyPress={scrollRight}
          canScroll={canScrollRight()}
        />
      </OuterWrapper>
    </ColumnWrapper>
  );

  const uploadSection = (
    <ColumnWrapper width="40%">
      <SectionHeader>{i18n.__('hub_image_upload_section_header')}</SectionHeader>
      <Wrapper>
        <UploadButton>
          <ImageSelector fileService={fileService} onUpload={onUpload} onLoading={onLoading} />
        </UploadButton>
        <Preview>
          {uploading ? (
            <Placeholder icon={SpinningIcon} />
          ) : (
            <HubImage
              tabIndex={0}
              title={i18n.__('hub_image_select_image')}
              src={getImagePath(newImage)}
              onClick={() => newImage && selectImage(newImage)}
              onKeyPress={() => newImage && selectImage(newImage)}
            />
          )}
        </Preview>
      </Wrapper>
    </ColumnWrapper>
  );

  const editSection = (
    <ColumnWrapper width="60%">
      <SectionHeader>{i18n.__('hub_image_edit_section_header')}</SectionHeader>
      <Wrapper>
        <ImageCropper imageUrl={getOriginalFilePath(selectedHubImage)} onCrop={setPreview} setBlob={setBlob} />
        <PreviewCrop>
          <HubImage
            title={i18n.__('hub_image_selected_image')}
            alt={i18n.__('hub_image_selected_image')}
            src={preview}
          />
        </PreviewCrop>
      </Wrapper>
    </ColumnWrapper>
  );

  const bottomSection = (
    <BottomWrapper>
      {uploadSection}
      {editSection}
    </BottomWrapper>
  );

  const contentPanel = (
    <ColumnWrapper>
      {generalSection}
      {userSection}
      {bottomSection}
    </ColumnWrapper>
  );

  const buttonLabelKey = window?.editor?.useAutoSave ? 'app_save' : 'app_ok';

  return (
    <Popup
      popupIsOpen={popupIsOpen}
      title={i18n.__('hub_image_general_section_header')}
      id="hub-image-manager-popup"
      contentPanel={contentPanel}
      buttonLabel={i18n.__(buttonLabelKey)}
      onClose={onClose}
      onClick={uploadAndSave}
      width={1026}
      snapWidth={snapWidth}
    />
  );
};

export default HubImageManager;
